import React, { useState } from 'react';
import { BoltIcon } from '@heroicons/react/24/outline';
import { TWButton, TWConfirmModal } from '../../../../components/ui';
import { groupBlockers } from '../../../connect/connect-readiness';

// The five reasons a restaurant comes off direct charges, plus room to say what
// actually happened. Free text alone tends to arrive as "test"; a code alone
// cannot be read six months later. The audit trail stores the human label.
export const DEACTIVATE_REASONS = [
  'Restaurant asked',
  'Payment problem',
  'Dispute or risk',
  'Activated by mistake',
  'Other',
];

const ConnectActivation = ({
  readiness,
  isActive = false,
  loadError = '',
  // False until the first readiness read has settled (success OR failure).
  // Before that, `readiness` is null and `loadError` is '' — the exact same
  // shape a genuinely empty, ready:false response would have — so this
  // section cannot otherwise tell "not read yet" from "nothing is wrong".
  loaded = true,
  busy = '',
  hasAccount = false,
  onActivate,
  onDeactivate,
  onRetry,
  onRecreate,
}) => {
  const [confirming, setConfirming] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');

  const ready = !!readiness?.ready;
  const groups = groupBlockers(readiness?.blockers);
  // Only meaningful for the not-yet-active (Activate) branch below — an
  // active company's Deactivate control must stay available regardless of
  // whether readiness has loaded (see the "still offers Deactivate" rule
  // this section already follows for `loadError`).
  const checking = !isActive && !loaded && !loadError;

  // Whichever explanation is actually on screen right now for why Activate
  // is disabled — used both to choose what renders in place of the blocker
  // list and to point the button's own aria-describedby at it, so a
  // screen-reader user learns why, not just a visual admin.
  const disabledReasonId = loadError
    ? 'connect-activation-load-error'
    : checking
      ? 'connect-activation-checking'
      : groups.length > 0
        ? 'connect-activation-blockers'
        : undefined;

  // `!!loadError` and `checking` are both spelled out explicitly rather than
  // folded into `ready` — a stale `readiness` object can carry `ready: true`
  // from an earlier successful check while the section above is right now
  // saying "couldn't check" or "checking", and Activate must not read as
  // enabled just because `readiness` itself is non-null.
  const activateDisabled = !ready || !!busy || !!loadError || checking;

  // Spec 2026-09-12 §4: fees.payer cannot change on an existing account. An
  // account Stripe bills Thisway for can never pass readiness; the only fix
  // is a new account that pays its own Stripe fees, which is the admin's
  // call and destructive enough to confirm. Hidden once the company is
  // live: Deactivate first.
  const canRecreate =
    hasAccount &&
    loaded &&
    !loadError &&
    readiness?.feePayerVerified === false &&
    readiness?.paymentModel === 'LEGACY_PLATFORM';

  const closeModal = () => {
    setConfirming('');
    setReason('');
    setNote('');
  };

  const confirmDeactivate = () => {
    const text = note.trim() ? `${reason} — ${note.trim()}` : reason;
    closeModal();
    onDeactivate?.(text);
  };

  return (
    <section className="py-6">
      <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
        Direct charges
      </h3>

      {loadError ? (
        // Unknown is not blocked. Say the check could not be run rather than
        // rendering an empty blocker list, which reads as "nothing is wrong".
        // A dead end is not an answer either: offer the retry, per spec section 7.
        <div className="mt-3 max-w-2xl">
          <p id="connect-activation-load-error" className="text-sm text-slate-600 dark:text-slate-300">
            {loadError}
          </p>
          {onRetry && (
            <TWButton
              variant="default"
              size="sm"
              label="Check again"
              className="mt-2"
              onClick={() => onRetry()}
            />
          )}
        </div>
      ) : (
        <>
          <p className="mt-3 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
            {isActive
              ? 'Charges from kiosks, the website and the app settle on this restaurant’s own Stripe account.'
              : // A company born Pending (D5/D7) takes no card payments at all —
                // "still settle on Thisway's account" is a legacy-only claim and
                // false for it. Legacy wording is unchanged.
                readiness?.paymentModel === 'CONNECT_PENDING'
                ? 'This company takes no card payments until you activate direct charges.'
                : 'Charges still settle on Thisway’s account. Activating changes that for future orders only.'}
          </p>

          {checking ? (
            // The first readiness read has not settled yet. Rendering
            // nothing here — the pre-fix behaviour — left an empty blocker
            // list and a disabled Activate with no explanation, the exact
            // defect class this whole section exists to eliminate.
            <p
              id="connect-activation-checking"
              className="mt-4 text-sm text-slate-500 dark:text-slate-400"
            >
              Checking whether this company is ready to activate direct charges…
            </p>
          ) : (
            groups.length > 0 && (
              <div id="connect-activation-blockers" className="mt-4 space-y-4">
                {groups.map((group) => (
                  <div key={group.key}>
                    <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                      {group.title}
                    </p>
                    <ul className="mt-2 space-y-1.5">
                      {group.items.map((item) => (
                        <li
                          key={item.code}
                          className="flex flex-col gap-0.5 text-sm sm:flex-row sm:gap-4"
                        >
                          <span className="text-slate-900 dark:text-slate-100 sm:w-96 sm:shrink-0">
                            {item.label}
                          </span>
                          {item.hint && (
                            <span className="text-slate-500 dark:text-slate-400">
                              {item.hint}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )
          )}
        </>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        {isActive ? (
          <TWButton
            variant="default"
            icon={BoltIcon}
            label="Deactivate"
            loading={busy === 'deactivate'}
            disabled={!!busy}
            onClick={() => setConfirming('deactivate')}
          />
        ) : (
          // `primary`, matching the tab's own existing primary actions
          // (connect-tab.js:507, :569). The one-coloured-element rule is about
          // status signalling — dots, badges, banners that narrate state — not
          // about affordances. Activate is the thing an admin opened this tab
          // to do; demoting it to slate hides it among the row buttons.
          <TWButton
            variant="primary"
            icon={BoltIcon}
            label="Activate direct charges"
            loading={busy === 'activate'}
            disabled={activateDisabled}
            aria-describedby={activateDisabled ? disabledReasonId : undefined}
            onClick={() => setConfirming('activate')}
          />
        )}
        {canRecreate && (
          <TWButton
            variant="default"
            label="Re-create Stripe account"
            loading={busy === 'recreate'}
            disabled={!!busy}
            onClick={() => setConfirming('recreate')}
          />
        )}
      </div>

      {/* Reversible by design — Deactivate exists — so a plain confirm, not a
          typed one. The sentence is the ticket's, verbatim. */}
      <TWConfirmModal
        open={confirming === 'activate'}
        onClose={closeModal}
        onConfirm={() => {
          closeModal();
          onActivate?.();
        }}
        title="Activate direct charges"
        description="Future kiosk/website/mobile charges will go to the restaurant Stripe account. Historical orders stay on Thisway Stripe / Mercury."
        confirmLabel="Activate"
        tone="warning"
      />

      <TWConfirmModal
        open={confirming === 'deactivate'}
        onClose={closeModal}
        onConfirm={confirmDeactivate}
        title="Deactivate direct charges"
        description={
          // A company born Pending never had Mercury, so Deactivate puts it
          // back to Pending, not on Thisway's account (owner, 2026-09-25).
          readiness?.deactivateModel === 'CONNECT_PENDING'
            ? 'Card payments stop until you activate direct charges again. Orders already taken on the restaurant’s account stay there.'
            : 'Future charges return to Thisway’s account. Orders already taken on the restaurant’s account stay there.'
        }
        confirmLabel="Deactivate"
        tone="danger"
        confirmDisabled={!reason}
      >
        <fieldset className="mt-4 space-y-2">
          <legend className="text-xs font-semibold text-slate-600 dark:text-slate-300">
            Why?
          </legend>
          {DEACTIVATE_REASONS.map((option) => (
            <label key={option} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="deactivate-reason"
                aria-label={option}
                checked={reason === option}
                onChange={() => setReason(option)}
              />
              <span className="text-slate-700 dark:text-slate-200">{option}</span>
            </label>
          ))}
          <textarea
            aria-label="Add a note"
            placeholder="Add a note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            // 450, not 500: the assembled string is "<reason> — <note>", and
            // the server rejects a reason over 500 characters with a 400. The
            // longest preset reason is "Activated by mistake" (20 chars) plus
            // the " — " separator, so 450 keeps the assembled string
            // comfortably under the limit no matter which reason is picked.
            maxLength={450}
            className="mt-2 w-full rounded-md border border-slate-300 p-2 text-sm dark:border-slate-600 dark:bg-slate-800"
          />
        </fieldset>
      </TWConfirmModal>

      <TWConfirmModal
        open={confirming === 'recreate'}
        onClose={closeModal}
        onConfirm={() => {
          closeModal();
          onRecreate?.();
        }}
        title="Re-create the Stripe account"
        description="Stripe bills this account’s fees to Thisway, so it can never go live. A new account that pays its own Stripe fees replaces it: the restaurant has to finish Stripe onboarding again and the terminals have to be prepared again. Orders already taken stay on the old account."
        confirmLabel="Re-create"
        tone="danger"
      />
    </section>
  );
};

export default ConnectActivation;
